import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook for managing navigation state and logic
 *
 * Provides:
 * - Expanded items state management
 * - Navigation handlers for main items and sub-items
 * - Active route detection
 * - Navigation functions
 */
export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({});

  /**
   * Handle click on main navigation item
   * If item has children, navigate to parent path and toggle expansion;
   * otherwise navigate to path
   */
  const handleItemClick = useCallback(
    item => {
      if (item.children && item.children.length > 0) {
        // Navigate to parent path (not first child)
        navigate(item.path);
        // Also toggle expansion
        setExpandedItems(prev => ({
          ...prev,
          [item.id]: !prev[item.id],
        }));
      } else {
        navigate(item.path);
      }
    },
    [navigate]
  );

  /**
   * Handle click on sub-navigation item
   * Navigate to the sub-item's path
   */
  const handleSubItemClick = useCallback(
    subItem => {
      navigate(subItem.path);
    },
    [navigate]
  );

  /**
   * Check if a path is currently active
   * Matches exact path or paths that start with the given path
   */
  const isActive = useCallback(
    path => {
      return (
        location.pathname === path || location.pathname.startsWith(path + '/')
      );
    },
    [location.pathname]
  );

  /**
   * Check if an item is currently expanded
   */
  const isExpanded = useCallback(
    itemId => {
      return expandedItems[itemId] || false;
    },
    [expandedItems]
  );

  /**
   * Navigate to a specific path
   */
  const navigateTo = useCallback(
    path => {
      navigate(path);
    },
    [navigate]
  );

  return {
    expandedItems,
    setExpandedItems,
    handleItemClick,
    handleSubItemClick,
    isActive,
    isExpanded,
    navigateTo,
    currentPath: location.pathname,
  };
};
